import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Pricing", item: "https://cashmyproperty.ai/pricing" },
  ],
};

const plans = [
  {
    step: "Entry Level",
    title: "Feasibility Report Fee",
    desc: "A fixed fee for a detailed AI-generated Potential & Feasibility Report covering zoning, HBU analysis, financial projections, brand matches, and ranked monetization strategies.",
    price: "₹4,999",
    priceSub: "per plot (~$200–$500)",
    cta: "Get Report",
    featured: false,
    features: ["Full HBU scoring across 6 categories", "Zoning & FSI compliance analysis", "Top 3 monetization strategies ranked", "5-year ROI projections", "Brand / developer shortlist", "Expert review by Govianu Group", "48-hour PDF delivery"],
  },
  {
    step: "Success Fee",
    title: "Deal Brokerage",
    desc: "We earn only when you succeed. For JDAs we charge 1–2% of deal value. For commercial leases, 1–2 months' rent. No upfront risk — pay only on deal closure.",
    price: "1–2%",
    priceSub: "of deal value",
    cta: "Start Deal",
    featured: true,
    features: ["1–2% for Joint Development Agreements", "1–2 months' rent for commercial leasing", "Partner identification & matching", "Site visit facilitation", "Deal negotiation & legal coordination", "JDA / Lease agreement drafting", "Pay only upon successful closure"],
  },
  {
    step: "Optional",
    title: "Project Management",
    desc: "For landowners who want full oversight — we monitor construction progress, ensure JDA compliance, manage contractor deliverables, and send monthly progress reports.",
    price: "2–3%",
    priceSub: "of project cost",
    cta: "Learn More",
    featured: false,
    features: ["Construction milestone tracking", "JDA compliance monitoring", "Contractor coordination", "Monthly progress reports", "Dispute resolution support"],
  },
  {
    step: "Specialized",
    title: "Franchise Consulting",
    desc: "Fee for securing a brand franchise or retail outlet placement on your land. We leverage relationships with fuel companies, QSR chains, and supermarket brands actively seeking sites.",
    price: "Custom",
    priceSub: "per franchise deal",
    cta: "Inquire",
    featured: false,
    features: ["Brand franchise placement", "Fuel station / EV hub licensing", "QSR chain site acquisition", "Supermarket / retail outlet setup", "Lease negotiation with brand", "Property value uplift 15–25%"],
  },
];

const faqs = [
  { q: "Will I lose ownership of my land?", a: "Absolutely not. All our strategies are designed to retain your ownership. Whether it's a JDA, long-term lease, or franchise placement — your name stays on the title deed." },
  { q: "Is the feasibility report refundable?", a: "The ₹4,999 feasibility fee is non-refundable as it involves significant AI compute and expert review. However, this fee is adjusted against our success fee if you proceed with a deal." },
  { q: "When exactly do I pay the success fee?", a: "Only after the deal is fully signed and the first payment from the partner is received. Not before. Not on verbal agreements." },
  { q: "What if the AI report doesn't find a use for my land?", a: "In rare cases where no viable monetization path exists, we provide a detailed explanation and suggest improvements (legal cleanup, infrastructure wait) to improve future prospects." },
  { q: "Can NRIs pay for the report from abroad?", a: "Yes. We accept international payments via Stripe and PayPal. NRIs can also use their NRO/NRE accounts via NEFT/RTGS." },
  { q: "Is there a minimum land size?", a: "We work with plots as small as 2,000 sq.ft for retail/franchise strategies, and 10,000 sq.ft+ for JDA and warehouse leasing. Larger plots unlock more monetization options." },
];

export default function PricingPage() {
  const { getContent } = usePageContent('pricing');
  const faqHeading = getContent('pricing-faq-heading') || 'Pricing FAQs';

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <PageHero
        badge="Revenue Model"
        title="Transparent"
        highlight="Pricing"
        subtitle="Four revenue streams. Owner-first model. We succeed only when you succeed."
        image="https://readdy.ai/api/search-image?query=Panoramic%20aerial%20view%20of%20modern%20Indian%20city%20skyline%20at%20twilight%20with%20golden%20lights%20illuminating%20commercial%20buildings%20and%20highways%2C%20warm%20sunset%20sky%20with%20deep%20teal%20and%20amber%20tones%2C%20cinematic%20urban%20landscape%20photography%2C%20metropolitan%20Bengaluru%20cityscape%2C%20dramatic%20perspective%2C%20high%20detail%20editorial%20quality&width=1800&height=800&seq=pricing-hero-page-v2&orientation=landscape&nocache=true"
      />

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Pricing</span>
          </nav>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start max-w-6xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.title}
                className={`relative p-8 rounded-2xl h-full ${
                  plan.featured
                    ? "bg-background-200 border-2 border-primary-500 scale-[1.02]"
                    : "card-dark"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-bold uppercase whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{plan.step}</span>
                <h3 className="font-display font-semibold text-xl text-foreground-950 mt-3 mb-3">{plan.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-2xl font-display font-bold text-foreground-950">{plan.price}</span>
                  <span className="text-sm text-foreground-500 ml-1">{plan.priceSub}</span>
                </div>
                <ul className="space-y-2 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line text-primary-500 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block w-full text-center py-3 rounded-full font-semibold text-sm transition-colors ${
                    plan.featured
                      ? "bg-primary-500 text-white hover:bg-primary-600"
                      : "border border-primary-300/50 text-primary-700 hover:bg-primary-500/10"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {[
              { icon: "ri-shield-check-line", label: "100% Ownership Retained" },
              { icon: "ri-lock-2-line", label: "Data Confidential" },
              { icon: "ri-award-line", label: "15+ Years Experience" },
              { icon: "ri-thumb-up-line", label: "Pay Only on Success" },
            ].map((badge) => (
              <div key={badge.label} className="flex items-center gap-3 p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                <div className="w-9 h-9 flex items-center justify-center shrink-0">
                  <i className={`${badge.icon} text-xl text-primary-600`} />
                </div>
                <span className="text-sm text-foreground-600 font-medium">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">{faqHeading}</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.q} className="p-6 rounded-2xl card-dark">
                <h4 className="font-display font-semibold text-foreground-950 mb-2 flex items-start gap-2">
                  <i className="ri-question-line text-primary-500 shrink-0 mt-0.5" />
                  {faq.q}
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed pl-6">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}